/**
 * SignaturePad Component
 * רכיב חתימה דיגיטלית
 * מאפשר ציור חתימה, ניקוי ושמירה כתמונה
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';

const SignaturePad = ({
  width = 400,
  height = 150,
  lineColor = '#000000',
  lineWidth = 2,
  backgroundColor = '#ffffff',
  required = false,
  label = 'חתימה',
  onSignatureChange,
  initialSignature = null,
  disabled = false,
  showBorder = true,
  animate = false, // Animate when signature is required but empty
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Set canvas dimensions
    canvas.width = width;
    canvas.height = height;

    // Fill background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Load initial signature if provided
    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = initialSignature;
    }
  }, [width, height, backgroundColor, initialSignature]);

  // Get position from event
  const getPosition = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches && e.touches[0]) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);

  // Draw line
  const draw = useCallback((start, end) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }, [lineColor, lineWidth]);

  // Handle start drawing
  const handleStart = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();

    const pos = getPosition(e);
    setLastPosition(pos);
    setIsDrawing(true);
  }, [disabled, getPosition]);

  // Handle drawing
  const handleMove = useCallback((e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();

    const pos = getPosition(e);
    draw(lastPosition, pos);
    setLastPosition(pos);
    setHasSignature(true);
  }, [isDrawing, disabled, getPosition, lastPosition, draw]);

  // Handle stop drawing
  const handleEnd = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();

    setIsDrawing(false);

    // Notify parent of signature change
    if (onSignatureChange) {
      const canvas = canvasRef.current;
      if (canvas) {
        onSignatureChange(canvas.toDataURL('image/png'));
      }
    }
  }, [isDrawing, onSignatureChange]);

  // Clear signature
  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);

    if (onSignatureChange) {
      onSignatureChange(null);
    }
  }, [backgroundColor, onSignatureChange]);

  // Get signature as data URL
  const getSignatureData = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return null;
    return canvas.toDataURL('image/png');
  }, [hasSignature]);

  // Export methods via ref
  React.useImperativeHandle(
    React.forwardRef(() => canvasRef),
    () => ({
      clear: clearSignature,
      getSignatureData,
      hasSignature: () => hasSignature
    }),
    [clearSignature, getSignatureData, hasSignature]
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      direction: 'rtl'
    }}>
      {/* Label */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontWeight: 500,
        color: '#181818'
      }}>
        <span style={{ fontSize: '16px' }}>✍️</span>
        <span>{label}</span>
        {required && <span style={{ color: '#c23934' }}>*</span>}
      </div>

      {/* Canvas Container */}
      <div style={{
        position: 'relative',
        width: width,
        maxWidth: '100%'
      }}>
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: height,
            border: showBorder ? `2px ${animate && required && !hasSignature ? 'dashed' : 'solid'} ${animate && required && !hasSignature ? '#e74c3c' : '#d8dde6'}` : 'none',
            borderRadius: '8px',
            cursor: disabled ? 'not-allowed' : 'crosshair',
            touchAction: 'none',
            backgroundColor: backgroundColor,
            animation: animate && required && !hasSignature ? 'signaturePulse 2s infinite' : 'none'
          }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />

        {/* Placeholder text when empty */}
        {!hasSignature && !disabled && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#706e6b',
            fontSize: '14px',
            pointerEvents: 'none',
            textAlign: 'center'
          }}>
            חתום כאן
          </div>
        )}

        {/* Clear button */}
        {hasSignature && !disabled && (
          <button
            onClick={clearSignature}
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              background: 'rgba(255,255,255,0.9)',
              border: '1px solid #d8dde6',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            type="button"
          >
            <span>🗑️</span>
            <span>נקה</span>
          </button>
        )}
      </div>

      {/* Required warning */}
      {required && !hasSignature && (
        <div style={{
          fontSize: '12px',
          color: '#c23934',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>⚠️</span>
          <span>נדרשת חתימה</span>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes signaturePulse {
          0%, 100% { border-color: #e74c3c; }
          50% { border-color: #f39c12; }
        }
      `}</style>
    </div>
  );
};

// Wrapper component with ref forwarding
const SignaturePadWithRef = React.forwardRef((props, ref) => {
  const internalRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = internalRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = props.backgroundColor || '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    },
    getSignatureData: () => {
      const canvas = internalRef.current;
      if (!canvas) return null;
      return canvas.toDataURL('image/png');
    },
    isEmpty: () => {
      const canvas = internalRef.current;
      if (!canvas) return true;
      const ctx = canvas.getContext('2d');
      const pixelBuffer = new Uint32Array(
        ctx.getImageData(0, 0, canvas.width, canvas.height).data.buffer
      );
      // Check if all pixels are the background color
      const bgColor = props.backgroundColor || '#ffffff';
      return !pixelBuffer.some(color => color !== 0xffffffff);
    }
  }));

  return <SignaturePad {...props} />;
});

export default SignaturePad;
export { SignaturePadWithRef };
